// saveClient — Studio config persistence. The original implementation only POSTed to the Vite dev
// save-bridge (/__studio/config/*). That works on the loopback dev server but FAILS on the static
// deploy (Cloudflare Pages): the bridge routes don't exist, so the SPA fallback returns index.html and
// `res.json()` throws "Unexpected token '<'". It also meant the public site never reflected saved edits.
//
// Persistence model now (works everywhere):
//   - SAVE  -> localStorage (always; the source of truth for on-device edits) + best-effort dev-bridge
//              POST (only in dev, so the hub's canonical config/studio/*.json stays updated). The bridge
//              is wrapped so a non-JSON / missing response NEVER throws.
//   - LOAD  -> localStorage  >  dev-bridge (disk, dev only)  >  baked seed (config/studio at build time)
//              >  code defaults. The baked seed is what makes the public site boot to the owner's rules.
import { useEffect } from 'react';
import { RuleSetSchema } from '@mj/protocol';
import type { RuleSet, YakuDefMeta } from '@mj/protocol';
import seed from 'virtual:mj-studio-seed';
import { useStudioApi } from './useStore';

const LS_RULESET = 'mj.studio.ruleSet';
const LS_CATALOG = 'mj.studio.catalog';
const isDev = import.meta.env.DEV;

// ---- localStorage (guarded: no-op/undefined when unavailable, e.g. SSR/tests/Safari private) -------
function lsSet(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
function lsGet(key: string): unknown {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : undefined;
  } catch {
    return undefined;
  }
}

function asRuleSet(v: unknown): RuleSet | undefined {
  if (!v) return undefined;
  const parsed = RuleSetSchema.safeParse(v);
  return parsed.success ? (parsed.data as RuleSet) : undefined;
}
function asCatalog(v: unknown): YakuDefMeta[] | undefined {
  return Array.isArray(v) ? (v as YakuDefMeta[]) : undefined;
}

// ---- dev save-bridge (best-effort; never throws) ---------------------------------------------------
let cachedToken: string | null = null;
async function jsonFetch(input: string, init?: RequestInit): Promise<unknown | undefined> {
  try {
    const res = await fetch(input, init);
    if (!res.ok) return undefined;
    // Guard against the SPA fallback returning index.html (text/html) for a missing bridge route.
    if (!(res.headers.get('content-type') ?? '').includes('application/json')) return undefined;
    return await res.json();
  } catch {
    return undefined;
  }
}
async function getToken(): Promise<string | null> {
  if (cachedToken) return cachedToken;
  const j = (await jsonFetch('/__studio/token')) as { token?: string } | undefined;
  cachedToken = j?.token ?? null;
  return cachedToken;
}
async function bridgeSave(path: string, content: unknown): Promise<void> {
  if (!isDev) return;
  const token = await getToken();
  if (!token) return;
  await jsonFetch('/__studio/config/save', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, path, content }),
  });
}
async function bridgeLoad(): Promise<{ ruleSet?: RuleSet; catalog?: YakuDefMeta[] }> {
  if (!isDev) return {};
  const j = (await jsonFetch('/__studio/config/load')) as { ruleSet?: unknown; catalog?: unknown } | undefined;
  if (!j) return {};
  const out: { ruleSet?: RuleSet; catalog?: YakuDefMeta[] } = {};
  const rs = asRuleSet(j.ruleSet);
  const cat = asCatalog(j.catalog);
  if (rs) out.ruleSet = rs;
  if (cat) out.catalog = cat;
  return out;
}

// ---- public API ------------------------------------------------------------------------------------
export async function saveRuleSet(name: string, rs: RuleSet): Promise<{ ok: boolean; error?: string }> {
  const ok = lsSet(LS_RULESET, rs);
  await bridgeSave(`rulesets/${name}.json`, rs); // best-effort: keep the hub's canonical file in sync
  return ok ? { ok } : { ok: false, error: 'localStorage unavailable' };
}

/** Persist the WORKING catalog (custom DSL yaku live here, so it must survive a reload). */
export async function saveCatalog(catalog: YakuDefMeta[]): Promise<{ ok: boolean; error?: string }> {
  const ok = lsSet(LS_CATALOG, catalog);
  await bridgeSave('catalog.json', catalog);
  return ok ? { ok } : { ok: false, error: 'localStorage unavailable' };
}

/**
 * Boot-time load. Precedence: localStorage (freshest on-device edit) > dev-bridge disk (dev only) >
 * baked seed (the owner's config/studio at build time — makes the public site show their rules) > {}.
 * Never throws; returns {} when nothing is found so the store keeps its code defaults.
 */
export async function loadConfig(): Promise<{ ruleSet?: RuleSet; catalog?: YakuDefMeta[] }> {
  const out: { ruleSet?: RuleSet; catalog?: YakuDefMeta[] } = {};
  const lsRs = asRuleSet(lsGet(LS_RULESET));
  const lsCat = asCatalog(lsGet(LS_CATALOG));
  if (lsRs) out.ruleSet = lsRs;
  if (lsCat) out.catalog = lsCat;

  if (!out.ruleSet || !out.catalog) {
    const bridge = await bridgeLoad();
    if (!out.ruleSet && bridge.ruleSet) out.ruleSet = bridge.ruleSet;
    if (!out.catalog && bridge.catalog) out.catalog = bridge.catalog;
  }
  if (!out.ruleSet || !out.catalog) {
    const seedRs = asRuleSet(seed.ruleSet);
    const seedCat = asCatalog(seed.catalog);
    if (!out.ruleSet && seedRs) out.ruleSet = seedRs;
    if (!out.catalog && seedCat) out.catalog = seedCat;
  }
  return out;
}

/**
 * Subscribe to the dev server's config-changed event. On the browser this hot-reloads the store
 * (bumps revision so panels re-evaluate). No-op outside the Vite dev runtime.
 */
export function useConfigSaveListener(): void {
  const store = useStudioApi();
  useEffect(() => {
    const hot = import.meta.hot;
    if (!hot) return;
    const onChange = () => {
      store.setState((s) => ({ revision: s.revision + 1 }));
    };
    hot.on('studio:config-changed', onChange);
    return () => {
      hot.off('studio:config-changed', onChange);
    };
  }, [store]);
}
